import { FC, useContext } from "react";

import {
  AssessmentOutlined,
  DashboardOutlined,
  Handshake,
  SettingsOutlined,
} from "@mui/icons-material";
import {
  IconButton,
  Theme,
  Tooltip,
  useMediaQuery,
  ListItemIcon,
  ListItemText,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { NavLink } from "react-router-dom";

import { AuthStateContext } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";

import { collapsedAppIconSx } from "./collapsedAppIconSx";
import { frameNavItemSx } from "./frameNavItemSx";

interface Props {
  dense?: boolean;
  logout?: () => void;
  onMenuClick?: () => void;
  collapsed?: boolean;
}

const RubiPartnerMenu: FC<Props> = ({ dense = false, collapsed = false }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { setCollapsed } = useSidebar();
  const { currentUser, getOrgIDFromURL } = useContext(AuthStateContext);
  const segment = getOrgIDFromURL();
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const partnerOrgId =
    currentUser?.partner_org_id ??
    (segment && uuidRegex.test(segment) ? segment : undefined);
  const reportsPath = partnerOrgId
    ? `/rubipartner/${partnerOrgId}/reports`
    : "/rubipartner/reports";

  const hideSideBar = () => {
    setCollapsed(true);
  };

  const menuItems: {
    to?: string;
    text: string;
    icon: JSX.Element;
    disabled?: boolean;
  }[] = [
    {
      to: reportsPath,
      text: "Reports",
      icon: <AssessmentOutlined />,
    },
    // Placeholders — surfaced but greyed out until these views are built.
    {
      text: "Analytics",
      icon: <DashboardOutlined />,
      disabled: true,
    },
    {
      text: "Settings",
      icon: <SettingsOutlined />,
      disabled: true,
    },
  ];

  const menuItemSx = {
    pl: collapsed ? 1 : 2,
    py: dense ? 1 : 1.5,
    minHeight: collapsed ? 48 : "auto",
    justifyContent: collapsed ? "center" : "flex-start",
  };

  const menuItemContent = (item: (typeof menuItems)[number]) => (
    <>
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 40,
          mr: collapsed ? 0 : 2,
          justifyContent: "center",
        }}
      >
        {item.icon}
      </ListItemIcon>
      {!collapsed && <ListItemText primary={item.text} />}
    </>
  );

  return (
    <div className="menu">
      {!collapsed ? (
        <div className="sidebar-top">
          <div className="app-name">
            Rubi<span>PARTNER</span>
          </div>
        </div>
      ) : (
        <div className="application-item">
          <Tooltip title="Partner Reporting" placement="right" arrow>
            <IconButton
              aria-label="RubiPARTNER App"
              size="large"
              disableRipple
              sx={collapsedAppIconSx}
            >
              <Handshake />
            </IconButton>
          </Tooltip>
        </div>
      )}
      <div className="left-menu">
        {menuItems.map((item) => (
          <Tooltip
            key={item.text}
            title={collapsed ? item.text : ""}
            placement="right"
            arrow
          >
            {item.disabled ? (
              // Wrapped in a span so the collapsed tooltip still shows on the
              // disabled (pointer-events: none) item.
              <span>
                <MenuItem disabled sx={[frameNavItemSx(), menuItemSx]}>
                  {menuItemContent(item)}
                </MenuItem>
              </span>
            ) : (
              <MenuItem
                component={NavLink}
                to={item.to!}
                onClick={isSmall ? hideSideBar : undefined}
                sx={[frameNavItemSx(), menuItemSx]}
              >
                {menuItemContent(item)}
              </MenuItem>
            )}
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default RubiPartnerMenu;
